/**
 * SSO Complete Page
 *
 * Landningssida efter lyckad SSO-callback (OIDC/SAML). Backend har redan satt
 * accessToken/refreshToken som (js-läsbara) cookies och redirectar hit. Här
 * hydreras auth-storen (user + tokens) via /api/users/me och användaren
 * navigeras vidare till slutmålet – så att hen kommer rakt in i systemet i
 * stället för att mötas av login-rutan.
 */

import React, { useEffect, useRef, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import Cookies from 'js-cookie';
import { useAuthActions } from '@/store/authStore';
import { userService } from '@/services/user/userService';
import { usePageTitle } from '@/hooks/usePageTitle';
import { useTranslation } from '@/store/languageStore';

/**
 * Sanera redirect-målet till en intern app-sökväg (skydd mot open redirect).
 * Tillåter endast samma-origin; faller annars tillbaka till "/".
 */
const sanitizeRedirect = (raw: string | null): string => {
  if (!raw) return '/';
  try {
    const url = new URL(raw, window.location.origin);
    if (url.origin !== window.location.origin) return '/';
    const path = `${url.pathname}${url.search}${url.hash}`;
    return path.startsWith('/') ? path : '/';
  } catch {
    return raw.startsWith('/') ? raw : '/';
  }
};

const SsoComplete: React.FC = () => {
  const { t } = useTranslation();
  usePageTitle(t('auth.ssoCompleting'));
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { setLoginDetails } = useAuthActions();
  const [error, setError] = useState<string | null>(null);
  const hasRun = useRef(false);

  useEffect(() => {
    if (hasRun.current) return;
    hasRun.current = true;

    const redirectTo = sanitizeRedirect(searchParams.get('redirect'));

    const complete = async () => {
      try {
        const accessToken = Cookies.get('accessToken');
        const refreshToken = Cookies.get('refreshToken');

        if (!accessToken || !refreshToken) {
          throw new Error('missing_tokens');
        }

        // Hämta inloggad användare via cookie-sessionen (api-klienten skickar
        // Bearer + cookies). Hydrerar store + localStorage.
        const profile = await userService.getProfile();

        setLoginDetails({
          accessToken,
          refreshToken,
          user: {
            id: profile.id,
            email: profile.email,
            first_name: profile.first_name,
            last_name: profile.last_name,
            role: profile.role ?? undefined,
            role_name: profile.role_name ?? undefined,
            roleCodes: profile.roleCodes ?? [],
            organization_id: profile.organization_id,
            preferred_language: profile.preferred_language,
            auth_method: profile.auth_method,
            is_global_admin: profile.is_global_admin,
            can_impersonate_users: profile.can_impersonate_users === true
              || profile.can_impersonate_users === 1
              || profile.can_impersonate_users === '1',
            is_crm_partner: profile.is_crm_partner === true
              || profile.is_crm_partner === 1
              || profile.is_crm_partner === '1',
            MobilePhone: profile.MobilePhone,
          },
        });

        navigate(redirectTo, { replace: true });
      } catch {
        setError(t('auth.ssoCompleteError') || 'Inloggningen kunde inte slutföras. Försök igen.');
      }
    };

    void complete();
  }, [navigate, searchParams, setLoginDetails, t]);

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
        <div className="bg-slate-800 border border-red-500/30 rounded-xl p-8 max-w-md w-full mx-4">
          <div className="text-center">
            <div className="text-red-500 text-4xl mb-4">⚠️</div>
            <h2 className="text-xl font-semibold text-white mb-2">
              {t('auth.loginError') || 'Inloggningsfel'}
            </h2>
            <p className="text-slate-400 mb-6">{error}</p>
            <button
              onClick={() => navigate('/login', { replace: true })}
              className="px-6 py-2 bg-cyan-600 hover:bg-cyan-700 text-white rounded-lg transition-colors"
            >
              {t('auth.backToLogin') || 'Tillbaka till inloggning'}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
      <div className="text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cyan-500 mx-auto mb-4"></div>
        <p className="text-slate-300">{t('auth.ssoCompleting') || 'Loggar in...'}</p>
      </div>
    </div>
  );
};

export default SsoComplete;
